// ─────────────────────────────────────────────────────────────────────────────
// useAllTradesForAnalytics
//
// Fetches ALL of the current user's finished trades in the given window,
// across every account. Used by analytics surfaces (currently the Market
// page's "Your Trading Around Events" panel) where we need to support a
// real "All Accounts" view.
//
// Why this exists separately from useSupabase:
//   useSupabase scopes to the currently-selected account for the rest of
//   the app's needs (Dashboard, Trade Review, Journal). For analytics we
//   need the union across all accounts so users can compare per-account
//   behavior without losing the bigger picture.
//
// Pagination:
//   Supabase's project-level max-rows is 1000 by default — every API
//   response is capped there regardless of `limit=` in the query string
//   or `Range:` request header. To get all trades for active users with
//   high trade counts, we loop fetches of 1000 rows at a time using the
//   Range header until we hit a partial response (signalling end of data).
//
//   The MAX_PAGES safety cap stops the loop at 10,000 total trades
//   regardless. Past that, the analytics produce noise anyway and we'd
//   rather degrade gracefully than hammer the API.
//
// RLS already scopes by user_id, so the fetch is safe with no explicit
// filter — Supabase will only return rows the authenticated user owns.
//
// Returns:
//   { trades: array, loading: bool, error: string|null }
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from "react";
import { SUPABASE_URL, authFetch } from "../lib/supabase";

const PAGE_SIZE = 1000;   // matches Supabase's max-rows default
const MAX_PAGES = 10;     // safety ceiling: never fetch more than 10k trades

export function useAllTradesForAnalytics(daysBack = 180) {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const cutoff = new Date(Date.now() - daysBack * 86400000)
      .toISOString().split("T")[0];

    // Minimal column set — analytics only needs date, account, pnl/fees/commissions.
    // Larger payloads would slow each paginated request.
    const baseUrl = `${SUPABASE_URL}/rest/v1/trades` +
      `?select=trade_date,account_id,pnl,fees,commissions` +
      `&pnl=not.is.null` +
      `&trade_date=gte.${cutoff}` +
      `&order=trade_date.asc`;

    // Paginate through up to MAX_PAGES batches. Stop early when a page
    // returns fewer than PAGE_SIZE rows (signalling we've hit the end).
    async function fetchAll() {
      const all = [];
      for (let page = 0; page < MAX_PAGES; page++) {
        if (cancelled) return null;
        const from = page * PAGE_SIZE;
        const to   = from + PAGE_SIZE - 1;

        const res = await authFetch(baseUrl, {
          headers: { Range: `${from}-${to}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status} on page ${page}`);

        const batch = await res.json();
        all.push(...batch);

        // Partial page = we've reached the end of the data.
        if (batch.length < PAGE_SIZE) break;
      }
      return all;
    }

    fetchAll()
      .then(rows => {
        if (cancelled || rows === null) return;
        setTrades(rows);
        setLoading(false);
      })
      .catch(e => {
        if (cancelled) return;
        setError(e.message);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [daysBack]);

  return { trades, loading, error };
}
